import { LINE_BREAK_STYLES } from '@src/editor/services/captionLineBreakStyle';
import { DEFAULT_CAPTION_FONT } from '@src/utility/captionFonts';
import z from 'zod';

export const KaraokeConfigSchema = z.object({
  id: z.string(),
  width: z.string(),
  font_size: z.string(),
  font_weight: z.string(),
  fill_color: z.string(),
  background_color: z.string().nullable(),
  background_align_threshold: z.string().optional(),
  background_border_radius: z.string().optional(),
  text_wrap: z.boolean(),
  x_alignment: z.string(),
  x: z.string().nullable(),
  y: z.string(),
  line_height: z.string(),
  font_family: z.string(),
  font_style: z.string().optional(),
  text_transform: z.string().optional(),
  stroke_color: z.string(),
  stroke_width: z.string(),
  letter_spacing: z.string().optional(),
  animations: z.array(z.unknown()),
  hideComma: z.boolean(),
  hidePeriod: z.boolean(),
  hideFillers: z.boolean(),
  // Video-template "Lock Position": when true, every caption line this config
  // produces is emitted non-interactive (Creatomate `locked`). Lives on the
  // config (not the clip) so it survives karaoke regeneration on transcript
  // edits — regenerated clips inherit the same config. See KaraokeClip.toJSON.
  locked: z.boolean().optional(),
  // PLAT-11689 — the Cap toggle's "captions off": when true, every caption line
  // this config produces is emitted invisible (Creatomate `visible: false`), so
  // the lines stay produced and come back unchanged on Show. On the config, not
  // the clip, for the same reason as `locked`: regenerated clips inherit it.
  hidden: z.boolean().optional(),
  // Caption language. `'original'` renders the source-language transcript
  // (legacy and default). Any BCP 47 code (`'es'`, `'fr'`, …) routes the
  // produce path through TranslationStore for phrase-boundary captions
  // (PLAT-9621). Legacy `'english'` value remains valid for the deprecated
  // SubtitlesProcessor flow until T48/T49 retire it.
  language: z.string(),
  shadow_color: z.string().nullable().optional(),
  shadow_blur: z.number().nullable().optional(),
  shadow_x: z.number().nullable().optional(),
  shadow_y: z.number().nullable().optional(),
  transcript_effect: z
    .enum([
      'color',
      'karaoke',
      'highlight',
      'fade',
      'bounce',
      'slide',
      'enlarge',
    ])
    .optional(),
  transcript_split: z.enum(['none', 'word', 'line']).optional(),
  transcript_placement: z.enum(['static', 'animate', 'align']).optional(),
  transcript_maximum_length: z.number().nullable().optional(),
  // Unset = break by `transcript_maximum_length`; a Punctuation style
  // overrides it (see captionLineBreakStyle.ts).
  line_break_style: z.enum(LINE_BREAK_STYLES).nullable().optional(),
  transcript_color: z.string().optional(),
  parent_template_id: z.string().optional(),
  // PLAT-11409 — set when the font was auto-substituted for a non-Latin
  // caption language (epic PLAT-11407 DE1). Carries the user's own font so a
  // later switch to a Latin language restores it, and chained non-Latin
  // switches keep keying the preset-substitute table off the original family
  // instead of drifting through generic fallbacks.
  font_before_substitution: z
    .object({
      font_family: z.string(),
      font_weight: z.string().optional(),
      font_style: z.string().optional(),
      // The family WE substituted in. The marker is only "active" while the
      // config still carries exactly this family — a later manual pick
      // deactivates it, so restores and chained substitutions never override
      // a user's own choice.
      applied_font_family: z.string(),
    })
    .optional(),
});

export type KaraokeConfig = z.infer<typeof KaraokeConfigSchema>;

export const DEFAULT_EDITOR_KARAOKE_CONFIG: Omit<KaraokeConfig, 'id'> = {
  ...DEFAULT_CAPTION_FONT,
  width: '76%',
  font_size: '3.333vh',
  fill_color: 'white',
  background_color: 'transparent',
  background_align_threshold: '0%',
  background_border_radius: '0%',
  text_wrap: true,
  x_alignment: '50%',
  y: '86%',
  x: '50%',
  line_height: '125%',
  stroke_color: 'rgba(0, 0, 0, 1)',
  stroke_width: '1 vmin',
  letter_spacing: '50%',
  animations: [],
  hideComma: false,
  hidePeriod: false,
  hideFillers: false,
  language: 'original',
  shadow_color: null,
  shadow_blur: null,
  shadow_x: null,
  shadow_y: null,
  text_transform: 'uppercase',
};
